// Tiny fetch wrapper. We do NOT depend on a heavy client (axios, etc.) —
// the API surface is small and direct fetch + Tanstack Query is enough.

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly url: string,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
  }
}

export async function api<T>(
  url: string,
  init?: RequestInit,
): Promise<T> {
  // credentials: "include" sends the bunapp_session cookie on every
  // request (Phase 5 auth). Required because the SPA + API share an
  // origin in prod (bunapp.olombelona.com) but not in dev (Vite at
  // :5173 → uvicorn at :8000 via the dev proxy).
  //
  // FormData bodies must NOT carry a hand-set Content-Type — the
  // browser fills in `multipart/form-data; boundary=…` itself.
  const isFormData =
    typeof FormData !== "undefined" && init?.body instanceof FormData;
  const baseHeaders: Record<string, string> = isFormData
    ? {}
    : { "Content-Type": "application/json" };
  const r = await fetch(url, {
    credentials: "include",
    ...init,
    headers: {
      ...baseHeaders,
      ...(init?.headers ?? {}),
    },
  });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    const body = parseErrorBody(text, r.headers.get("content-type"));
    const message =
      body &&
      typeof body === "object" &&
      "detail" in body &&
      typeof body.detail === "string"
        ? body.detail
        : text || `HTTP ${r.status}`;
    throw new ApiError(r.status, url, message, body);
  }
  if (r.status === 204) return undefined as unknown as T;
  return (await r.json()) as T;
}


function parseErrorBody(
  text: string,
  contentType: string | null,
): unknown {
  if (!text || !contentType?.toLowerCase().includes("application/json")) {
    return undefined;
  }
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}
